import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Users2,
  BarChart3,
  FileCheck2,
  Building2,
  LineChart,
  Lock,
  Layers,
} from "lucide-react";
import TenantCrest from "../components/TenantCrest";

/**
 * Education360 — Premium Institutional Landing Page.
 * Consolidated Directive Workstream A. Presentation-layer redesign only.
 * Preserves: tenant routing, Highlands entry, authentication, tenant-isolation
 * architecture, THOM360 attribution.
 */
export default function Education360Landing() {
  const [tenants, setTenants] = useState(() => {
    try {
      const cached = sessionStorage.getItem("edu360.tenants");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && Date.now() - parsed.ts < 5 * 60 * 1000) return parsed.data || [];
      }
    } catch (_) {}
    return [];
  });
  const [loaded, setLoaded] = useState(() => {
    try { return Boolean(sessionStorage.getItem("edu360.tenants")); } catch (_) { return false; }
  });

  useEffect(() => {
    let alive = true;
    api.tenants()
      .then(({ data }) => {
        if (!alive) return;
        setTenants(data || []);
        try { sessionStorage.setItem("edu360.tenants", JSON.stringify({ ts: Date.now(), data })); } catch (_) {}
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  const capabilities = [
    { icon: BarChart3, title: "Academic Intelligence", body: "Subject-level attainment, term trajectories and cohort insight, drawn from measured marks-book evidence." },
    { icon: LineChart, title: "Executive Dashboards", body: "Board-ready snapshots of academic, attendance, behavioural and operational performance for the leadership team." },
    { icon: Users2, title: "Whole-Student Development", body: "Behaviour, punctuality, participation and learning-disposition indicators, with transparent evidence disclosure." },
    { icon: Sparkles, title: "AI Executive Commentary", body: "Structured, evidence-grounded narrative synthesised from real academic data — never fabricated observations." },
    { icon: FileCheck2, title: "Trusted Digital Reports", body: "A4 print-ready student performance reports, digitally verifiable via HMAC-SHA256 and QR authentication." },
    { icon: Building2, title: "Multi-Institutional", body: "Every school runs as its own tenant with its own subdomain, branding, users, roles and archives." },
  ];

  return (
    <div className="edu360-root min-h-screen" data-testid="edu360-landing">
      {/* NAV */}
      <header className="edu360-nav" data-testid="landing-nav">
        <div className="edu360-nav-inner">
          <div className="edu360-brand">
            <TenantCrest tenant={tenants[0] || {}} size="md" testId="landing-crest" />
            <div className="edu360-brand-text">
              <div className="edu360-brand-title">EDUCATION<span className="tm">360™</span></div>
              <div className="edu360-brand-sub">Institutional Intelligence Platform</div>
            </div>
          </div>
          <nav className="edu360-nav-links" aria-label="Primary">
            <a href="#capabilities" data-testid="nav-capabilities">Platform</a>
            <a href="#institutions" data-testid="nav-institutions">Institutions</a>
            <a href="#trust" data-testid="nav-trust">Trust &amp; Governance</a>
            <a href="#reporting" data-testid="nav-reporting">Reporting</a>
          </nav>
          <div className="edu360-nav-actions">
            <span className="edu360-nav-attr">A THOM Digital Infrastructure platform</span>
            <Link to="/education360/highlands" className="edu360-btn edu360-btn-primary" data-testid="landing-cta-nav">
              Access Your Institution
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="edu360-hero" data-testid="landing-hero">
        <div className="edu360-hero-inner">
          <div className="edu360-hero-left">
            <div className="edu360-eyebrow">The Intelligence Platform for Independent Schools</div>
            <h1 className="edu360-h1">
              Every learner understood.<br/>
              <span className="accent">Every decision informed.</span>
            </h1>
            <p className="edu360-lead">
              One integrated institutional environment connecting academic performance,
              school operations, executive intelligence and trusted reporting — inside
              a tenant-isolated architecture that each institution owns and controls.
            </p>
            <div className="edu360-hero-ctas">
              <Link to="/education360/highlands" className="edu360-btn edu360-btn-primary large" data-testid="landing-cta-highlands">
                Access Your Institution
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#reporting" className="edu360-btn edu360-btn-ghost large" data-testid="landing-cta-explore">Explore the Platform</a>
            </div>
            <ul className="edu360-credibility" aria-label="Platform architecture">
              <li><Lock className="w-3.5 h-3.5" /> Tenant-isolated institutional environments</li>
              <li><Layers className="w-3.5 h-3.5" /> Role-based intelligence, one platform</li>
              <li><FileCheck2 className="w-3.5 h-3.5" /> Integrated executive reporting</li>
            </ul>
          </div>

          <div className="edu360-hero-right" aria-hidden="true">
            <div className="edu360-dash">
              <div className="edu360-dash-head">
                <div className="edu360-dash-title">Executive Snapshot</div>
                <div className="edu360-dash-illus">Illustrative · not live institutional data</div>
              </div>
              <div className="edu360-dash-kpis">
                <div className="edu360-kpi"><div className="v">83.7%</div><div className="l">Overall</div></div>
                <div className="edu360-kpi"><div className="v">95%</div><div className="l">Attendance</div></div>
                <div className="edu360-kpi"><div className="v">85</div><div className="l">Behaviour</div></div>
              </div>
              <div className="edu360-dash-section">
                <div className="edu360-dash-lbl">Subject performance</div>
                {[
                  ["Computer Science", 94],
                  ["Mathematics", 89],
                  ["English Literature", 86],
                  ["Biology", 82],
                  ["Physics", 78],
                ].map(([s, v]) => (
                  <div className="edu360-bar-row" key={s}>
                    <span className="edu360-bar-label">{s}</span>
                    <span className="edu360-bar-track">
                      <span className="edu360-bar-fill" style={{ width: `${v}%`, background: v >= 80 ? "#166534" : "#C9A227" }}/>
                    </span>
                    <span className="edu360-bar-val">{v}%</span>
                  </div>
                ))}
              </div>
              <div className="edu360-dash-foot">
                <span>E360 · v1.0 · Illustrative</span>
                <span className="edu360-dash-status">Digitally verifiable</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="edu360-caps" data-testid="landing-capabilities">
        <div className="edu360-section-head">
          <div className="edu360-eyebrow">Capabilities</div>
          <h2 className="edu360-h2">From institutional data to executive intelligence</h2>
        </div>
        <div className="edu360-caps-grid">
          {capabilities.map((c) => (
            <article key={c.title} className="edu360-cap-card" data-testid={`cap-${c.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <c.icon className="w-6 h-6" style={{ color: "#C9A227" }} />
              <h3>{c.title}</h3>
              <p>{c.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* INSTITUTIONS */}
      <section id="institutions" className="edu360-inst" data-testid="landing-institutions">
        <div className="edu360-section-head">
          <div className="edu360-eyebrow">Institutional Access</div>
          <h2 className="edu360-h2">Your institution. Your intelligence environment.</h2>
          <p className="edu360-lead subdued">
            Each institution operates within its own branded environment, with access to its
            authorised dashboards, reports and intelligence capabilities.
          </p>
        </div>
        <div className="edu360-inst-grid">
          {!loaded && tenants.length === 0 && (
            <div className="edu360-inst-card skeleton" data-testid="tenant-skeleton">
              <div className="edu360-inst-crest" />
              <div className="edu360-inst-body">
                <div className="ln lg" /><div className="ln sm" />
              </div>
            </div>
          )}
          {loaded && tenants.length === 0 && (
            <div className="edu360-inst-empty" data-testid="tenant-empty">
              No institutional environments are currently published.
            </div>
          )}
          {tenants.map((t) => (
            <Link key={t.id} to={`/education360/${t.slug}`} className="edu360-inst-card" data-testid={`tenant-card-${t.slug}`}>
              <div
                className="edu360-inst-crest"
                style={t.branding?.crest_url ? { backgroundImage: `url('${t.branding.crest_url}')` } : undefined}
                aria-hidden="true"
              >
                {!t.branding?.crest_url && (t.short_name || "S").slice(0, 2).toUpperCase()}
              </div>
              <div className="edu360-inst-body">
                <div className="edu360-inst-name">{t.name}</div>
                <div className="edu360-inst-meta">{t.country} · {t.slug}.education360.thomdi.com</div>
              </div>
              <ArrowRight className="edu360-inst-arrow w-4 h-4" />
            </Link>
          ))}
        </div>
      </section>

      {/* TRUST */}
      <section id="trust" className="edu360-trust" data-testid="landing-trust">
        <div className="edu360-trust-inner">
          <div className="edu360-trust-head">
            <div className="edu360-eyebrow gold">Governance &amp; Digital Trust</div>
            <h2 className="edu360-h2 light">Institutional independence.<br/>Enterprise-grade governance.</h2>
            <p className="edu360-lead light">
              Every institutional environment is tenant-isolated by design. Reports are
              cryptographically bound to their issuing school and independently verifiable.
            </p>
          </div>
          <div className="edu360-trust-grid">
            {[
              { t: "Tenant Isolation", b: "Institutional data, users and archives are separated by design at every layer of the stack." },
              { t: "Role-Based Access", b: "Principals, teachers, parents, boarding staff, finance and executives each see only their authorised view." },
              { t: "Report Verification", b: "Every issued report carries a verification ID, QR code and HMAC-SHA256 signature." },
              { t: "Approval Workflow", b: "Reports move through a controlled approval state — never presented as approved until they are." },
              { t: "Non-Mutating Preview", b: "Preview renders never overwrite the authoritative archive of previously issued reports." },
              { t: "Data Integrity", b: "Determinism controls ensure identical bytes across parent, student, staff and archive channels." },
            ].map((x) => (
              <div className="edu360-trust-card" key={x.t}>
                <ShieldCheck className="w-5 h-5" style={{ color: "#C9A227" }} />
                <div className="t">{x.t}</div>
                <div className="b">{x.b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REPORTING */}
      <section id="reporting" className="edu360-reporting" data-testid="landing-reporting">
        <div className="edu360-reporting-inner">
          <div className="edu360-reporting-left">
            <div className="edu360-eyebrow">Executive Reporting</div>
            <h2 className="edu360-h2">A single-page executive record for every learner.</h2>
            <p className="edu360-lead subdued">
              The Education360 Student Performance Report distils academic attainment,
              attendance, behaviour, whole-student development and executive commentary
              onto one institutionally-branded A4 page — digitally verifiable, evidence-
              disclosed and role-controlled.
            </p>
            <ul className="edu360-report-features">
              <li>Institutional masthead with school crest &amp; motto</li>
              <li>Subject-level attainment with teacher notes</li>
              <li>Attendance donut with full reconciliation</li>
              <li>Behaviour matrix with honest evidence disclosure</li>
              <li>Executive intelligence brief across five panels</li>
              <li>Principal's Assessment with amber approval-state disclosure</li>
              <li>Digital-trust footer with QR, HMAC-SHA256, verification ID</li>
            </ul>
          </div>
          <div className="edu360-reporting-right">
            <div className="edu360-report-frame">
              <div className="edu360-report-mock">
                <div className="rm-mast">
                  <div className="rm-crest" />
                  <div className="rm-title">
                    <div className="rm-title-a">EDUCATION360™</div>
                    <div className="rm-title-b">Student Performance Report</div>
                  </div>
                  <div className="rm-ref">
                    <div className="k">REF</div>
                    <div className="v">E360-…</div>
                  </div>
                </div>
                <div className="rm-row">
                  <div className="rm-panel">Profile</div>
                  <div className="rm-panel">AI Summary</div>
                  <div className="rm-panel">KPIs</div>
                </div>
                <div className="rm-table">
                  {[94, 89, 86, 85, 84, 82, 80, 78, 74].map((v, i) => (
                    <div className="rm-tr" key={i}>
                      <span className="rm-td name" />
                      <span className="rm-td bar"><span style={{ width: `${v}%`, background: v >= 80 ? "#166534" : "#C9A227" }} /></span>
                      <span className="rm-td val">{v}%</span>
                    </div>
                  ))}
                </div>
                <div className="rm-row">
                  <div className="rm-panel">Attendance 95%</div>
                  <div className="rm-panel">Strengths / Focus</div>
                  <div className="rm-panel">Behaviour</div>
                </div>
                <div className="rm-foot">
                  <span>Digitally verified · HMAC-SHA256</span>
                  <span>Generated · Awaiting Approval</span>
                </div>
              </div>
              <div className="edu360-report-illus">Illustrative preview · Actual report renders via authenticated institutional access.</div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="edu360-final-cta" data-testid="landing-final-cta">
        <div className="edu360-final-cta-inner">
          <h2 className="edu360-h2">A clearer view of every learner.<br/><span className="accent">A stronger foundation for every decision.</span></h2>
          <p className="edu360-lead">
            Discover an integrated intelligence environment designed to support academic
            excellence, student development and institutional leadership.
          </p>
          <div className="edu360-hero-ctas center">
            <Link to="/education360/highlands" className="edu360-btn edu360-btn-primary large" data-testid="landing-cta-final-highlands">
              Access Your Institution
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="edu360-footer" data-testid="landing-footer">
        <div className="edu360-footer-inner">
          <div className="edu360-footer-brand">
            <div className="edu360-footer-title">EDUCATION<span className="tm">360™</span></div>
            <div className="edu360-footer-sub">A THOM Digital Infrastructure platform</div>
          </div>
          <div className="edu360-footer-links">
            <a href="#capabilities">Platform</a>
            <a href="#institutions">Institutions</a>
            <a href="#trust">Governance</a>
            <a href="#reporting">Reporting</a>
          </div>
          <div className="edu360-footer-fine">
            The main THOM360 platform is not exposed through this URL. Institutional access
            is governed by tenant-scoped authentication.
          </div>
        </div>
      </footer>
    </div>
  );
}
